import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Loader2, Crown, CalendarDays, Copy, Check, Pencil, Trash2, LogOut,
  Send, UserMinus, Scissors
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const countdownLabel = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const n = Math.ceil((d - new Date()) / 86400000);
  if (n < 0) return 'Event passed';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  return `${n} days away`;
};

const Avatar = ({ src, name, size = 'w-9 h-9' }) => (
  src
    ? <img src={src} alt={name} className={`${size} rounded-full object-cover flex-shrink-0`} />
    : <div className={`${size} rounded-full bg-[var(--win-titlebar-start)] flex items-center justify-center flex-shrink-0`}>
        <span className="text-xs text-white font-bold">{name?.charAt(0)?.toUpperCase() || '?'}</span>
      </div>
);

const GroupDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myCosplays, setMyCosplays] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [charName, setCharName] = useState('');
  const [cosplayId, setCosplayId] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', event_name: '', event_date: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const me = group?.members?.find((m) => m.user_id === user?.id);

  const load = useCallback(async () => {
    try {
      const [g, cos, msgs] = await Promise.all([
        api.get(`/groups/${id}`),
        api.get('/cosplays'),
        api.get(`/groups/${id}/messages`),
      ]);
      setGroup(g.data);
      setMyCosplays(cos.data || []);
      setMessages(msgs.data || []);
      const mine = (g.data.members || []).find((m) => m.user_id === user?.id);
      setCharName(mine?.character_name || '');
      setCosplayId(mine?.cosplay_id || '');
    } catch (err) {
      if (err.response?.status === 403) toast.error('You are not a member of this group.');
      else toast.error('Failed to load group');
      navigate('/groups', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, navigate]);

  useEffect(() => { load(); }, [load]);

  const saveRole = async () => {
    setSavingRole(true);
    try {
      const res = await api.patch(`/groups/${id}/me`, { character_name: charName, cosplay_id: cosplayId });
      setGroup(res.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSavingRole(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const content = msg.trim();
    if (!content) return;
    setSending(true);
    try {
      const res = await api.post(`/groups/${id}/messages`, { content });
      setMessages((prev) => [...prev, res.data]);
      setMsg('');
    } catch (err) {
      toast.error('Could not send');
    } finally {
      setSending(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(group.join_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const openEdit = () => {
    setEditForm({
      name: group.name, description: group.description || '',
      event_name: group.event_name || '', event_date: group.event_date || '',
    });
    setShowEdit(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const res = await api.put(`/groups/${id}`, { ...editForm, event_date: editForm.event_date || null });
      setGroup(res.data);
      setShowEdit(false);
      toast.success('Group updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setSavingEdit(false);
    }
  };

  const removeMember = async (uid) => {
    try {
      await api.delete(`/groups/${id}/members/${uid}`);
      load();
    } catch (err) {
      toast.error('Could not remove member');
    }
  };

  const leaveGroup = async () => {
    try {
      await api.post(`/groups/${id}/leave`);
      toast.success('Left group');
      navigate('/groups', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not leave');
    }
  };

  const deleteGroup = async () => {
    if (!window.confirm('Delete this group for everyone? This cannot be undone.')) return;
    try {
      await api.delete(`/groups/${id}`);
      toast.success('Group deleted');
      navigate('/groups', { replace: true });
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  if (loading || !group) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const cd = countdownLabel(group.event_date);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="group-detail-page">
      <Link to="/groups" className="text-sm inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All groups
      </Link>

      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
              {group.is_owner && <Crown className="w-5 h-5 flex-shrink-0" />}
              {group.name}
            </h1>
            {group.description && <p className="mt-1">{group.description}</p>}
            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
              {group.event_name && <span>{group.event_name}</span>}
              {cd && <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {cd}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {group.is_owner ? (
              <>
                <Button onClick={openEdit} variant="outline" size="sm" className="gap-1">
                  <Pencil className="w-4 h-4" /> Edit
                </Button>
                <Button onClick={deleteGroup} variant="ghost" size="sm">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button onClick={leaveGroup} variant="ghost" size="sm" className="gap-1">
                <LogOut className="w-4 h-4" /> Leave
              </Button>
            )}
          </div>
        </div>

        {/* Readiness + invite */}
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span>Group readiness · {group.linked_count}/{group.member_count} linked</span>
              <span className="font-mono">{group.readiness}%</span>
            </div>
            <Progress value={group.readiness} className="h-2.5" />
          </div>
          {group.is_owner && group.join_code && (
            <div>
              <Label className="text-xs">Invite code</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 px-3 py-2 win-sunken font-mono text-sm truncate">{group.join_code}</code>
                <Button onClick={copyCode} variant="outline" size="sm">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs mt-1">Share this code. Members paste it on their Groups page to join.</p>
            </div>
          )}
        </div>
      </div>

      {/* Your role */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-heading font-semibold mb-4">Your role</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Character you're doing</Label>
            <Input value={charName} onChange={(e) => setCharName(e.target.value)} placeholder="e.g. Din Djarin" />
          </div>
          <div className="space-y-2">
            <Label>Link one of your cosplays</Label>
            <select value={cosplayId} onChange={(e) => setCosplayId(e.target.value)}
              className="w-full h-10 px-3 win-input text-sm cursor-pointer">
              <option value="">No linked cosplay</option>
              {myCosplays.map((c) => <option key={c.id} value={c.id}>{c.title} ({c.progress}%)</option>)}
            </select>
            <p className="text-xs">Linking a cosplay feeds the group's readiness from its progress.</p>
          </div>
        </div>
        <Button onClick={saveRole} disabled={savingRole} className="mt-4 font-semibold">
          {savingRole ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
      </div>

      {/* Roster */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-heading font-semibold mb-4">Squad ({group.member_count})</h2>
        <div className="space-y-3">
          {group.members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3 p-3 win-well">
              <Avatar src={m.profile_picture} name={m.display_name} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{m.display_name}</span>
                  {m.role === 'owner' && <Crown className="w-3.5 h-3.5 flex-shrink-0" />}
                </div>
                <p className="text-sm truncate">
                  {m.character_name || 'No character set'}
                  {m.cosplay_title && <span> · {m.cosplay_title}</span>}
                </p>
              </div>
              <div className="w-28 flex-shrink-0">
                {m.progress !== null ? (
                  <>
                    <div className="text-right text-xs font-mono mb-1">{m.progress}%</div>
                    <Progress value={m.progress} className="h-1.5" />
                  </>
                ) : (
                  <div className="text-right text-xs flex items-center justify-end gap-1"><Scissors className="w-3 h-3" /> not linked</div>
                )}
              </div>
              {group.is_owner && m.role !== 'owner' && (
                <button onClick={() => removeMember(m.user_id)} className="flex-shrink-0" title="Remove member">
                  <UserMinus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Discussion */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-heading font-semibold mb-4">Discussion</h2>
        <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
          {messages.length === 0 ? (
            <p className="text-sm text-center py-6">No messages yet. Say hi to your squad.</p>
          ) : messages.map((m) => (
            <div key={m.id} className="flex items-start gap-3">
              <Avatar src={m.user_profile_picture} name={m.user_name} size="w-8 h-8" />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium">{m.user_name}</span>
                  <span className="text-xs">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={sendMessage} className="flex gap-2">
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message your squad..." />
          <Button type="submit" disabled={sending}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>

      {/* Edit modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-heading">Edit group</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Group name *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={2} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event</Label>
                <Input value={editForm.event_name} onChange={(e) => setEditForm({ ...editForm, event_name: e.target.value })}
                  />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={editForm.event_date ? editForm.event_date.slice(0, 10) : ''}
                  onChange={(e) => setEditForm({ ...editForm, event_date: e.target.value })}
                  />
              </div>
            </div>
            <Button type="submit" disabled={savingEdit} className="w-full font-semibold">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupDetailPage;
